/**
 * Instrumentation for the Edge / mobile GHL age-dropdown freeze.
 * Tag: [DEBUG-age-freeze]
 *
 * Enable the on-screen HUD with `?harry_debug_survey=1` or
 * `localStorage.harry_debug_survey = "1"`. Logger events always fire.
 */

export const SURVEY_AGE_FREEZE_DEBUG_PREFIX = '[DEBUG-age-freeze]';
export const SURVEY_AGE_FREEZE_DEBUG_QUERY_KEY = 'harry_debug_survey';
export const SURVEY_AGE_FREEZE_DEBUG_STORAGE_KEY = 'harry_debug_survey';
export const SURVEY_AGE_FREEZE_DEBUG_EVENTS_KEY = 'harry-survey-age-freeze-debug';

export const SURVEY_AGE_FREEZE_DEBUG_EVENT_LIMIT = 20;
export const SURVEY_AGE_FREEZE_DEBUG_HUD_LIMIT = 8;

/** Hold Harry parent React updates so Edge DevTools / the age picker can settle. */
export const SURVEY_AGE_PARENT_UPDATE_DEFER_MS = 400;

export function shouldDeferSurveyParentUpdate(input: {
  age: string | null;
  postedHeight: number | null;
  lastAge: string | null;
}): boolean {
  return Boolean(input.age || (input.lastAge && input.postedHeight != null));
}

/** Fullscreen booking iframe is already 100dvh — GHL setHeight must not restyle Harry. */
export function shouldApplySurveyParentHeight(input: {
  fillViewport: boolean;
  postedHeight: number | null;
}): boolean {
  return input.postedHeight != null && !input.fillViewport;
}

export function isViteHotClient(hot?: unknown): boolean {
  if (arguments.length > 0) return Boolean(hot);
  return Boolean(typeof import.meta !== 'undefined' ? import.meta.hot : undefined);
}

export type SurveyAgeFreezeDebugSource =
  | 'postMessage'
  | 'viewport'
  | 'iframe-src'
  | 'mount'
  | 'outer-script';

/** Parent-page scripts Harry injects or that GHL uses to restyle the iframe. */
export const HARRY_OUTER_SURVEY_SCRIPT_HINTS = [
  'form_embed',
  'msgsndr',
  'leadconnectorhq',
  'ghl-form-embed',
] as const;

export interface SurveyIframeLayoutSnapshot {
  position: string | null;
  pointerEvents: string | null;
  zIndex: string | null;
  top: number | null;
  left: number | null;
  width: number | null;
  height: number | null;
  visualViewportHeight: number | null;
  innerHeight: number | null;
}

export interface SurveyAgeFreezeDebugEvent {
  at: string;
  source: SurveyAgeFreezeDebugSource;
  kind: string;
  age: string | null;
  income: string | null;
  postedHeight: number | null;
  currentHeight: number | null;
  willChangeHeight: boolean;
  willChangeSrc: boolean;
  action: string;
  messagesInLastSecond: number;
  iframePosition: string | null;
  iframeTop: number | null;
  iframeHeight: number | null;
  visualViewportHeight: number | null;
  innerHeight: number | null;
  pointerEvents: string | null;
  userAgentShort: string;
  inlineStyle?: string | null;
  className?: string | null;
  srcHost?: string | null;
  outerScripts?: string[];
  mutation?: string | null;
  ageSeen?: boolean;
  viteHot?: boolean;
}

export function isSurveyAgeFreezeDebugEnabled(
  search: string | undefined = typeof window !== 'undefined' ? window.location.search : undefined,
  storage: Storage | null | undefined = typeof window !== 'undefined' ? window.localStorage : undefined,
): boolean {
  if (search) {
    const value = new URLSearchParams(search).get(SURVEY_AGE_FREEZE_DEBUG_QUERY_KEY);
    if (value === '1' || value === 'true') return true;
    if (value === '0' || value === 'false') return false;
  }
  try {
    if (storage?.getItem(SURVEY_AGE_FREEZE_DEBUG_STORAGE_KEY) === '1') return true;
  } catch {
    /* private mode */
  }
  return false;
}

export function describeGhlMessageKind(data: unknown): string {
  if (Array.isArray(data)) {
    const head = data[0];
    if (typeof head === 'string' && head.trim()) return head;
    return `array:${data.length}`;
  }
  if (data && typeof data === 'object') {
    const type = (data as { type?: unknown }).type;
    if (typeof type === 'string' && type.trim()) return type;
    const keys = Object.keys(data as Record<string, unknown>);
    return keys.length ? `object:${keys.slice(0, 4).join(',')}` : 'object';
  }
  if (typeof data === 'string') {
    return data.length > 48 ? `${data.slice(0, 48)}…` : data;
  }
  return data == null ? 'null' : typeof data;
}

export function shortUserAgent(
  ua: string = typeof navigator !== 'undefined' ? navigator.userAgent : '',
): string {
  const edge = /Edg(?:e|A|iOS)?\/[\d.]+/.exec(ua)?.[0];
  const chrome = /Chrome\/[\d.]+/.exec(ua)?.[0];
  const mobile = /Mobile|Android|iPhone|iPad/i.test(ua);
  const engine = edge ?? chrome ?? (ua ? ua.slice(0, 28) : 'unknown');
  return `${engine} ${mobile ? 'mobile' : 'desktop'}`;
}

export function snapshotSurveyIframeLayout(
  iframe: HTMLIFrameElement | null,
): SurveyIframeLayoutSnapshot {
  if (typeof window === 'undefined' || !iframe) {
    return {
      position: null,
      pointerEvents: null,
      zIndex: null,
      top: null,
      left: null,
      width: null,
      height: null,
      visualViewportHeight:
        typeof window === 'undefined'
          ? null
          : Math.round(window.visualViewport?.height ?? window.innerHeight),
      innerHeight: typeof window === 'undefined' ? null : window.innerHeight,
    };
  }
  const style = window.getComputedStyle(iframe);
  const rect = iframe.getBoundingClientRect();
  return {
    position: style.position || null,
    pointerEvents: style.pointerEvents || null,
    zIndex: style.zIndex || null,
    top: Math.round(rect.top),
    left: Math.round(rect.left),
    width: Math.round(rect.width),
    height: Math.round(rect.height),
    visualViewportHeight: Math.round(window.visualViewport?.height ?? window.innerHeight),
    innerHeight: window.innerHeight,
  };
}

export function persistSurveyAgeFreezeDebugEvent(event: SurveyAgeFreezeDebugEvent): void {
  if (typeof window === 'undefined') return;
  try {
    const next = [...readSurveyAgeFreezeDebugEvents(), event].slice(
      -SURVEY_AGE_FREEZE_DEBUG_EVENT_LIMIT,
    );
    window.sessionStorage.setItem(SURVEY_AGE_FREEZE_DEBUG_EVENTS_KEY, JSON.stringify(next));
  } catch {
    /* private mode / quota */
  }
}

export function readSurveyAgeFreezeDebugEvents(): SurveyAgeFreezeDebugEvent[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.sessionStorage.getItem(SURVEY_AGE_FREEZE_DEBUG_EVENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as SurveyAgeFreezeDebugEvent[]) : [];
  } catch {
    return [];
  }
}

export function listHarryOuterSurveyScripts(
  scripts: ArrayLike<{ src?: string; id?: string }> = typeof document !== 'undefined'
    ? document.scripts
    : [],
): string[] {
  return Array.from(scripts)
    .map((script) => {
      const src = script.src?.trim() ?? '';
      const id = script.id?.trim() ?? '';
      if (!src && !id) return '';
      const haystack = `${id} ${src}`;
      if (!HARRY_OUTER_SURVEY_SCRIPT_HINTS.some((hint) => haystack.includes(hint))) {
        return '';
      }
      return id || src;
    })
    .filter((value): value is string => Boolean(value));
}

export function snapshotIframeDom(iframe: HTMLIFrameElement | null): {
  inlineStyle: string | null;
  className: string | null;
  srcHost: string | null;
} {
  if (!iframe) {
    return { inlineStyle: null, className: null, srcHost: null };
  }
  let srcHost: string | null = null;
  try {
    const url = new URL(iframe.src, typeof window !== 'undefined' ? window.location.href : undefined);
    srcHost = `${url.hostname}${url.pathname}`;
  } catch {
    srcHost = iframe.getAttribute('src');
  }
  return {
    inlineStyle: iframe.getAttribute('style'),
    className: iframe.className || null,
    srcHost,
  };
}

export function describeAttributeMutation(
  attributeName: string | null,
  oldValue: string | null,
  iframe: Pick<HTMLIFrameElement, 'getAttribute'>,
): string {
  const name = attributeName || 'unknown';
  const next = iframe.getAttribute(name);
  const previous = oldValue ?? '∅';
  const current = next ?? '∅';
  const trim = (value: string) => (value.length > 80 ? `${value.slice(0, 80)}…` : value);
  return `${name}: ${trim(previous)} -> ${trim(current)}`;
}

export function formatSurveyAgeFreezeDebugLine(event: SurveyAgeFreezeDebugEvent): string {
  if (event.source === 'outer-script') {
    return `OUTER ${event.kind} ${event.mutation ?? event.action}`;
  }
  const age = event.age ?? '—';
  const height = event.postedHeight != null ? `h=${event.postedHeight}` : 'h=—';
  const src = event.willChangeSrc ? 'srcSWAP' : 'srcStay';
  return `${event.kind} ${height} age=${age} ${src} ${event.action}`;
}
